import React, { useContext, useState, useEffect } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import DataTableViewOptions from "./data-table-view-options";
import { ListRestart, DownloadIcon, XCircle } from "lucide-react";
import { AppContext } from "contexts/AppContext";
import DataTableFacetedFilter from "./data-table-faceted-filter";

function formatDate(date) {
  return date instanceof Date && !isNaN(date.getTime())
    ? date.toISOString().split("T")[0]
    : ""; // Return an empty string for invalid dates
}

function DataTableToolbar({ table }) {
  const appContext = useContext(AppContext);
  const {
    dateRange,
    setDateRange,
    selectedClient,
    selectedUser,
    setSelectedUser,
    setAccountTypeFilter,
    activeTab,
  } = appContext;

  // Local state for search input to prevent overwriting
  const [searchValue, setSearchValue] = useState("");
  
  // Initialize search value from table filter
  useEffect(() => {
    if (!table) return;
    const userIdColumn = table.getColumn("user_id");
    const currentFilterValue = userIdColumn?.getFilterValue() || "";
    setSearchValue(currentFilterValue);
  }, [table]);
  
  // Update table filter when search value changes (with debouncing)
  useEffect(() => {
    if (!table) return;
    const userIdColumn = table.getColumn("user_id");
    if (userIdColumn) {
      userIdColumn.setFilterValue(searchValue);
    }
  }, [searchValue, table]);

  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    // Update local state immediately for responsive UI
    setSearchValue(newSearch);
  };

  const applyDateFilter = (startDate, endDate) => {
    // Date filtering is now handled entirely by the backend
    // No need to apply frontend date filters
    console.log('Date filter requested but handled by backend:', { startDate, endDate });
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    
    // Check if the value is a valid date
    const parsedDate = new Date(newStartDate);
    if (isNaN(parsedDate.getTime())) {
      console.error("Invalid start date:", newStartDate);
      setDateRange([null, dateRange[1]]);
      applyDateFilter(null, dateRange[1] ? formatDate(dateRange[1]) : null);
      return;
    }
  
    setDateRange([parsedDate, dateRange[1]]);
    applyDateFilter(newStartDate, dateRange[1] ? formatDate(dateRange[1]) : null);
  };
  
  const handleEndDateChange = (e) => {
    if (!e) return;
    const newEndDate = e.target.value;

    const parsedDate = new Date(newEndDate);
    if (isNaN(parsedDate.getTime())) {
      console.error("Invalid end date:", newEndDate);
      setDateRange([dateRange[0], null]);
      applyDateFilter(dateRange[0] ? formatDate(dateRange[0]) : null, null);
      return;
    }

    setDateRange([dateRange[0], new Date(newEndDate)]);
    applyDateFilter(dateRange[0] ? formatDate(dateRange[0]) : null, newEndDate);
  };

  const resetTable = () => {
    if (table) {
      table.resetSorting();
      table.resetColumnFilters();
      table.setGlobalFilter("");
      table.resetRowSelection();
      table.resetColumnVisibility();
    }
    setSelectedUser(null);
    
    // Clear the user_id column filter specifically
    if (table) {
      const userIdColumn = table.getColumn("user_id");
      if (userIdColumn) {
        userIdColumn.setFilterValue("");
      }
    }
    // Also clear the local search state
    setSearchValue("");
    
    // Clear the global account type filter
    if (setAccountTypeFilter) {
      setAccountTypeFilter(null);
    }
    
    // Reset the UI for the account type filter
    if (table) {
      const accountTypeColumn = table.getColumn("account_type");
      if (accountTypeColumn) {
        accountTypeColumn.setFilterValue(undefined);
      }
    }
    
    setDateRange([
      new Date(new Date().setDate(new Date().getDate() - 29)), // 30 days ago
      new Date() // Today
    ]);
  };  

  const handleClearUserFilter = () => {
    setSelectedUser(null); // Clear the selected user when clicking the "X"
  };

  const handleDownload = () => {
    if (!table) {
      console.warn("Download is unavailable because the table has not been initialized.");
      return;
    }
    // 1) Get the "real" columns you want to export (only visible, etc.)
    const visibleColumns = table
      .getAllColumns()
      .filter((col) => col.getIsVisible());
  
    // 2) Build an array of header names
    // (These come from columnDef.header or from col.id.)
    const headers = visibleColumns.map((col) => {
      return typeof col.columnDef.header === "string"
        ? col.columnDef.header
        : col.id;
    });
  
    // 3) Get the rows you want to export (filtered or all)
    const rows = table.getPrePaginationRowModel().rows;

    // 4) Convert each row to raw data, column by column
    const csvRows = rows.map((row) => {
      // row.original is typically the raw data object for that row
      // You can also do row.getValue(col.id) if col.accessorKey is set up
      return visibleColumns.map((col) => {
        const accessorKey = col.columnDef.accessorKey;
  
        // If we have an accessorKey, grab from row.original
        if (accessorKey) {
          return wrapAndEscapeCsvValue(row.original[accessorKey]);
        }
        // Otherwise we might try row.getValue(col.id) or just blank
        const cellValue = row.getValue(col.id);
        return wrapAndEscapeCsvValue(cellValue);
      }).join(",");
    });
  
    // 5) Combine headers + rows
    const csvString = [
      headers.join(","), // a header row
      ...csvRows
    ].join("\n");
  
    // 6) Trigger the CSV file download
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "table_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Utility to wrap values in quotes and escape existing quotes
  function wrapAndEscapeCsvValue(value) {
    if (value == null) return "";  // handle null/undefined
    const strValue = String(value);
    // Escape existing quotes by doubling them, then wrap in quotes
    return `"${strValue.replace(/"/g, '""')}"`;
  }  

  const filteredRowCount = table ? table.getFilteredRowModel().rows.length : 0;
  const searchPlaceholder = table
    ? `Search User ID in ${filteredRowCount} Rows...`
    : "Search User ID...";

  return (
    <div className="flex flex-col lg:flex-row items-center justify-between space-y-2 lg:space-y-0 lg:space-x-2">
      <div className="flex flex-col lg:flex-row flex-1 items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full">
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={handleSearchChange}
          className="h-8 w-full lg:w-[200px]"
        />
        <Input
          type="date"
          value={dateRange[0] ? formatDate(dateRange[0]) : ""}
          onChange={handleStartDateChange}
          placeholder="Start Date"
          className="h-8 w-full lg:w-[150px]"
        />
        <Input
          type="date"
          value={dateRange[1] ? formatDate(dateRange[1]) : ""}
          onChange={handleEndDateChange}
          placeholder="End Date"
          className="h-8 w-full lg:w-[150px]"
        />
        
        {/* Account Type Filter - only show for heritage and heritagepool clients */}
        {selectedClient &&
          activeTab !== "Traces" &&
          (selectedClient.key === "heritage" || selectedClient.key === "heritagepool") && 
          (() => {
            if (!table) {
              return null;
            }
            // Check if the column exists by looking at all available columns
            const hasAccountTypeColumn = table.getAllColumns().some(
              column => column.id === "account_type"
            );
            
            if (hasAccountTypeColumn) {
              return (
                <DataTableFacetedFilter
                  column={table.getColumn("account_type")}
                  title="Account Type"
                  options={[
                    { value: "Internal", label: "Internal" },
                    { value: "External (English)", label: "External (English)" },
                    { value: "External (Spanish)", label: "External (Spanish)" },
                  ]}
                  mutuallyExclusive={true}
                />
              );
            }
            return null;
          })()
        }
      </div>
      {/* <DataTableViewOptions table={table} /> */}

      {selectedUser && (
        <div className="flex items-center space-x-2 ml-auto">
          <span className="bg-blue-700 text-white h-8 text-xs px-2 py-1 rounded-full flex items-center">
            {selectedUser || "User Selected"}
            <button onClick={handleClearUserFilter} className="ml-2 text-white">
              <XCircle className="h-4 w-4" />
            </button>
          </span>
        </div>
      )}

      <Button
        variant="outline"
        onClick={() => resetTable()}
        className="h-8 px-2 lg:px-3 w-full lg:w-auto ml-auto"
      >
        <ListRestart className="mr-2 h-4 w-4" />
        Reset Filters
      </Button>
      <Button
        variant="outline"
        onClick={handleDownload}
        className="h-8 px-2 lg:px-3 w-full lg:w-auto ml-auto"
      >
        <DownloadIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default DataTableToolbar;
